import axios, { AxiosRequestConfig } from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh';

import store from '@/store'
import { setupCache } from 'axios-cache-interceptor'

import router from '@/router/index'

const api = setupCache(
  axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    withCredentials: true
  }),
  {
    debug: import.meta.env.MODE === 'development' ? console.log : null,
    // cache-specific options like ttl
    ttl: 1000 * 60 * 5 // 5 minutes
  }
)

const addToken = async (config: AxiosRequestConfig) => {
	
	if (!requiresAuth(config.url)) {
		return config
	}
	
    let token = store.state.auth.token
    
    const valid = store.getters.isRefreshTokenValid

	if (!valid || !token) {
		await store.dispatch('refreshToken').catch(e => console.log(e))
		token = store.getters.token
	}
    
    if (token) {
       
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
        
	    const impersonate = store.getters.isImpersonating ? store.getters.username : null
        
        if (impersonate) {
	        config.headers['X-Switch-User'] = impersonate
	        config.headers['X-Switch-User-Workspace'] = store.getters.workspaceUuid
        }
        
    }
    
    return Promise.resolve()
    
}

const cancellable = config => {

  if (!store.getters.abortController) {
  	store.commit('setAbortController', new AbortController()) // so the auth module can cancel pending requests on logout
  }

  if (!config.signal) {
     config.signal = store.getters.abortController.signal
  }
  
  return config
}

api.interceptors.request.use(
    async config => {
    	await addToken(config)
    	cancellable(config)
    	return config
    },
    error => Promise.reject(error),
)

const setAppMessageError = (config, message) => {
    if (!config?.silent) {
        store.commit('setAppMessageError', message)
    }
}

const publicUrls = [
	'/api/signup/init',
	'/api/signup/register',
	'/authentication_token',
	'/token/refresh',
	'/token/invalidate',
	'/email_verify',
	'/api/workspaces/workspace_settings/signup',
	'/api/workspaces-public/saml-settings',
]

const requiresAuth = url => {
	return !publicUrls.find(publicUrl => url.indexOf(publicUrl) > -1)
}

const httpErrorHandler = (error, config) => {   

    if (error === null) {
        
        throw new Error('Unrecoverable error!! Error is null!')
        
    } else if (axios.isAxiosError(error)) {
    
        const response = error?.response
        
        if (response) {         
            const statusCode = response?.status
            if (statusCode === 400) {                
                setAppMessageError(config, response?.data?.error?.detail ?? response?.statusText)
                throw new Error(response?.data?.error?.detail ?? response?.statusText, {cause: error})
            } else if (statusCode === 403) {                
                setAppMessageError(config, 'Request forbidden.')
                throw new Error('Request forbidden.', {cause: error})
			} else if (statusCode === 429) {                
                setAppMessageError(config, response?.data?.error?.detail ?? 'Too many requests')
                throw new Error(response?.data?.error?.detail ?? 'Too many requests', {cause: error})
            } else if (statusCode > 401 && statusCode < 500) {                
                // setAppMessageError(config, 'Request error')
                throw new Error('Request error', {cause: error})
            } else if (statusCode >= 500) {
            	// Don't show these as toast messages
            	console.log(response)             
                throw response?.data?.error?.detail ?? new Error('Server error', {cause: error})
            } else if (statusCode !== 401) {                
                setAppMessageError(config, response?.data?.error?.detail ?? 'Server error')
                throw response?.data?.error?.detail ?? new Error('Server error', {cause: error})
            }
        }
        
    }
    
    throw error

}

// this interceptor is used to handle all success ajax request
const responseHandler = (response) => {
	if (response.headers.link && store.getters.eventSourceUrl === null) {
		response.headers.link.split(',').forEach(link => {
			const linkParts = link.split(';')
			if (linkParts[1]?.trim() == 'rel="mercure"') {
				store.commit(
					'eventSourceUrl', 
					linkParts[0].replace('<', '').replace('>', '')
				)
			}
		})
		
	}
    return response
}
  
// This interceptor is used to handle failed request
const responseErrorHandler = (error) => {
    
    let config = null
    
    if (error.code === 'ERR_CANCELED') {
    	console.log(error)
    	return
    }
    
    if (error?.config) {
        config = error?.config ?? {}
        if (config?.raw) {
        	throw error
            // return Promise.reject(error)
        }
    }

    return httpErrorHandler(error, config)    
}

createAuthRefreshInterceptor(api, failedRequest => {

	// Don't try to refresh the JWT token if the failed request
	// is an attempt to refresh the JWT token
	
	const path = URL.parse(failedRequest.request.responseURL).pathname
	
	console.log('Intercepted request error.')
	console.log(failedRequest)
	
	// Don't try to run the refresh if we weren't fully authenticated in the first place

	if (path == '/token/refresh') {
		store.dispatch('logout') // Invalidate any existing credentials
		if (requiresAuth(path)) {
			store.getters.abortController.abort()
			store.commit('setAbortController', null)
			console.log(failedRequest)
			console.log('Unable to refresh, logging out.')
			router.push({name: 'Login'})
		}
		return				
	}

	return store.dispatch('refreshToken', {raw: true, silent: true}).catch(e => {			
		store.getters.abortController.abort()
		store.commit('setAbortController', null)
		console.log(e)
		console.log('Error refreshing, logging out.')
		store.dispatch('logout')
		router.push({name: 'Login'})			
	})
	
}/* , {
	shouldRefresh: (error) => {
		console.log(error)
		return true
	}
} */);

// Add a response interceptor
api.interceptors.response.use(responseHandler, responseErrorHandler)

export default api
